import type {ReactNode} from 'react'
import type React from 'react'
import {createContext, useContext, useEffect, useState, useMemo} from 'react'
import {GrowthBook} from '@growthbook/growthbook'
import type {FeatureFlags} from '@/services/growthbookService'
import {fetchFeaturesWithOkta, getFeatureFlags, type GrowthBookFeaturesParams} from '@/services/growthbookService'
import {useOktaAuth} from '@okta/okta-react'
import useApiRequest from '@/hooks/useApiRequest'

interface GrowthBookContextType {
  growthbook: GrowthBook | null
  features: FeatureFlags
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<void>
}

const GrowthBookContext = createContext<GrowthBookContextType | undefined>(undefined)

interface GrowthBookProviderProps {
  children: ReactNode
  apiHost: string
  clientKey: string
}

/**
 * GrowthBook Provider Component
 * Initializes GrowthBook SDK and provides feature flags to the entire application
 */
export const GrowthBookProvider: React.FC<GrowthBookProviderProps> = ({children, apiHost, clientKey}) => {
  const [growthbook, setGrowthbook] = useState<GrowthBook | null>(null)
  const [features, setFeatures] = useState<FeatureFlags>({})
  const {authState} = useOktaAuth()

  // Prepare params for useApiRequest - only when authenticated
  const params = useMemo<GrowthBookFeaturesParams | null>(
    () => (apiHost && clientKey && authState?.isAuthenticated ? {apiHost, clientKey} : null),
    [apiHost, clientKey, authState?.isAuthenticated],
  )

  // Use shared useApiRequest hook for fetching features
  // This will skip the API call until authState.isAuthenticated is true
  const {data: featuresData, isLoading, error, refetch} = useApiRequest(fetchFeaturesWithOkta, params)

  // Initialize GrowthBook instance when features data is available
  useEffect(() => {
    if (featuresData) {
      try {
        // Create GrowthBook instance
        const gb = new GrowthBook({
          enableDevMode: true,
        })

        // Set features from API response using setPayload (setFeatures is deprecated)
        const featureMap = featuresData.features || {}
        gb.setPayload({features: featureMap})
        setGrowthbook(gb)

        // Extract typed feature flags
        const flags = getFeatureFlags(gb)

        // If the flags object is empty but we have feature data in the API response,
        // create a fallback flags object using the defaultValues from the API response
        if (Object.keys(flags).length === 0 && Object.keys(featureMap).length > 0) {
          const fallbackFlags: FeatureFlags = {}

          // Extract defaultValue from each feature in the API response
          Object.entries(featureMap).forEach(([key, value]: [string, any]) => {
            if (value && typeof value === 'object' && 'defaultValue' in value) {
              fallbackFlags[key] = value.defaultValue
            }
          })

          setFeatures(fallbackFlags)
        } else {
          setFeatures(flags)
        }
      } catch (err) {
        console.error('Error initializing GrowthBook:', err)
        setFeatures({})
      }
    }

    // Cleanup GrowthBook instance on unmount
    return () => {
      if (growthbook) {
        growthbook.destroy()
      }
    }
  }, [featuresData])

  // Memoize the refetch callback to prevent recreation
  const memoizedRefetch = useMemo(
    () => async () => {
      refetch()
    },
    [refetch],
  )

  // Memoize context value to prevent unnecessary re-renders
  const value: GrowthBookContextType = useMemo(
    () => ({
      growthbook,
      features,
      isLoading,
      error,
      refetch: memoizedRefetch,
    }),
    [growthbook, features, isLoading, error, memoizedRefetch],
  )

  return <GrowthBookContext.Provider value={value}>{children}</GrowthBookContext.Provider>
}

/**
 * Custom hook to access GrowthBook feature flags
 * @returns GrowthBook context with features, loading state, and error
 */
export const useGrowthBook = (): GrowthBookContextType => {
  const context = useContext(GrowthBookContext)
  if (context === undefined) {
    throw new Error('useGrowthBook must be used within a GrowthBookProvider')
  }
  return context
}

/**
 * Custom hook to get a specific feature flag value
 * @param featureName - The name of the feature flag
 * @returns The feature flag value
 */
export const useFeatureFlag = <K extends keyof FeatureFlags>(featureName: K): FeatureFlags[K] => {
  const {features} = useGrowthBook()
  return features[featureName]
}
